import { useEffect, useRef, useState } from "react";
import { PlayCircle, ArrowDown, Unlock, Play, Volume2, VolumeX } from "lucide-react";

/**
 * VSL_URL — drop your finished video URL here.
 * Accepts:
 *   • Direct .mp4/.webm/.mov URL → renders <video> and auto-unlocks on end
 *   • Vimeo/YouTube/Wistia embed URL → renders <iframe>, unlocks via button
 * Leave empty to keep the placeholder and unlock immediately (no gate).
 */
/**
 * VSL_URL fallback — used when the parent doesn't pass a videoUrl prop.
 * Prefer passing the URL in via the market config for multi-region support.
 */
const DEFAULT_VSL_URL = "";
const VSL_POSTER = "";
const DEFAULT_STORAGE_KEY = "vsl_watched_v1";

export default function VSLBanner({ onUnlock, videoUrl, storageKey = DEFAULT_STORAGE_KEY }) {
  const url = videoUrl || DEFAULT_VSL_URL;
  const [ended, setEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  const markWatched = () => {
    try { localStorage.setItem(storageKey, "1"); } catch (e) { /* ignore */ }
    setEnded(true);
    onUnlock?.();
  };

  useEffect(() => {
    // If no video is configured, unlock instantly.
    if (!url) onUnlock?.();
  }, [url]);

  // Vimeo Player API: auto-unlock when the video finishes
  const isVimeo = /player\.vimeo\.com/i.test(url);
  useEffect(() => {
    if (!isVimeo || !iframeRef.current) return;
    let cancelled = false;
    let player = null;

    const attach = (Player) => {
      if (cancelled) return;
      try {
        player = new Player(iframeRef.current);
        playerRef.current = player;
        player.on("ended", () => markWatched());
        player.on("play",  () => setIsPlaying(true));
        player.on("pause", () => setIsPlaying(false));
      } catch (e) {
        // noop — user can still hit the manual "continue" button
      }
    };

    if (window.Vimeo && window.Vimeo.Player) {
      attach(window.Vimeo.Player);
    } else {
      const existing = document.querySelector('script[data-vimeo-player-api]');
      if (!existing) {
        const s = document.createElement("script");
        s.src = "https://player.vimeo.com/api/player.js";
        s.async = true;
        s.setAttribute("data-vimeo-player-api", "1");
        s.onload = () => window.Vimeo && attach(window.Vimeo.Player);
        document.body.appendChild(s);
      } else {
        existing.addEventListener("load", () => window.Vimeo && attach(window.Vimeo.Player));
      }
    }

    return () => {
      cancelled = true;
      try { player && player.off("ended"); } catch (e) { /* ignore */ }
    };
  }, []);

  const isDirectVideo = /\.(mp4|webm|mov)$/i.test(url || "");
  const isEmbed = url && !isDirectVideo;

  return (
    <section
      className="relative bg-zinc-950 text-white overflow-hidden"
      data-testid="vsl-banner"
    >
      <div className="absolute inset-0 grid-bg opacity-[0.06]" aria-hidden="true" />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14 relative">
        <div className="text-center max-w-2xl mx-auto mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-mono uppercase tracking-widest text-white/80">
            <PlayCircle className="w-3.5 h-3.5 text-indigo-400" />
            Watch first
          </div>
          <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
            Ticketing. Without the <span className="text-indigo-400">Ticket Tax</span>.
          </h2>
          <p className="mt-4 text-zinc-400 text-base sm:text-lg">
            Grab a brew. Put your feet up and watch the video below. Us and you are about to change the industry together.
          </p>
          <p className="mt-4 text-zinc-400 text-base sm:text-lg">
            Learn about us, who we are, what we&apos;re doing &amp; why we&apos;re doing it.
            <br />
            All of your questions answered, all of your concerns addressed. Just watch.
          </p>
        </div>

        <div
          className="relative w-full rounded-2xl overflow-hidden bg-zinc-900 shadow-2xl ring-1 ring-white/10"
          style={{ aspectRatio: "16 / 9" }}
          data-testid="vsl-frame"
        >
          {isEmbed && (
            <>
              <iframe
                ref={iframeRef}
                src={url}
                title="Affra — The Ticket Tax Stops Here"
                className="absolute inset-0 w-full h-full pointer-events-none"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
              />
              {/* Custom play/pause overlay — clicking anywhere on the video toggles playback */}
              <button
                type="button"
                onClick={() => {
                  const p = playerRef.current;
                  if (!p) return;
                  if (isPlaying) p.pause(); else p.play();
                }}
                data-testid="vsl-play-toggle"
                className="absolute inset-0 w-full h-full grid place-items-center bg-transparent focus:outline-none group"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {!isPlaying && (
                  <span className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/95 text-zinc-900 grid place-items-center shadow-2xl transition group-hover:scale-105">
                    <Play className="w-9 h-9 sm:w-10 sm:h-10 ml-1" fill="currentColor" />
                  </span>
                )}
              </button>
              {/* Mute toggle */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const p = playerRef.current;
                  if (!p) return;
                  const next = !isMuted;
                  p.setMuted(next).then(() => setIsMuted(next)).catch(() => {});
                }}
                data-testid="vsl-mute-toggle"
                className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-black/60 hover:bg-black/80 text-white text-xs font-medium backdrop-blur ring-1 ring-white/10"
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                <span>{isMuted ? "Unmute" : "Mute"}</span>
              </button>
            </>
          )}
          {isDirectVideo && (
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full"
              src={url}
              poster={VSL_POSTER || undefined}
              controls
              playsInline
              preload="metadata"
              onEnded={() => { setEnded(true); markWatched(); }}
            />
          )}
          {!url && (
            <div className="absolute inset-0 flex items-center justify-center" data-testid="vsl-placeholder">
              <div className="text-center px-6">
                <div className="mx-auto w-20 h-20 rounded-full bg-white/10 backdrop-blur grid place-items-center ring-1 ring-white/20">
                  <PlayCircle className="w-10 h-10 text-white/80" />
                </div>
                <div className="mt-6 font-display font-black text-2xl sm:text-3xl tracking-tight">
                  Video coming soon
                </div>
                <div className="mt-3 text-sm text-white/60 max-w-md mx-auto">
                  A walkthrough of how we&apos;re removing the ticket tax for venues.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Unlock button — only meaningful when a video is configured */}
        {url && (
          <div className="mt-6 flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={markWatched}
              data-testid="vsl-continue"
              className={`inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-semibold transition ${
                ended
                  ? "bg-indigo-500 text-white hover:bg-indigo-400"
                  : "bg-white/10 text-white hover:bg-white/20 border border-white/15"
              }`}
            >
              {ended ? (
                <>
                  <Unlock className="w-4 h-4" /> Continue to site
                </>
              ) : (
                <>
                  <ArrowDown className="w-4 h-4" /> I&apos;ve watched — continue
                </>
              )}
            </button>
            <div className="text-[11px] text-white/40">
              We only ask once. The rest of the site opens up after this.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/** Sync helper — hook that returns whether the visitor has already watched. */
export function useVSLUnlocked(marketKey) {
  const key = marketKey ? `vsl_watched_${marketKey}` : DEFAULT_STORAGE_KEY;
  const [unlocked, setUnlocked] = useState(() => {
    try { return localStorage.getItem(key) === "1"; } catch (e) { return false; }
  });
  return [unlocked, () => setUnlocked(true)];
}
